import React, { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Listbox, ListboxButton, ListboxOption, ListboxOptions, Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import { ChevronDownIcon, MagnifyingGlassIcon } from '@heroicons/react/20/solid';
import { classNames } from '../utils/classNames';

// The title of a page that is also a choice, with its list: the scope of the page ("All properties", one hotel).
// The title has the size of a page title (text-xl, semibold) with a chevron, as TitleSelectButton; a click opens
// the list of the choices under it. Each choice has a title, an optional second line and an optional icon; the
// selected choice is blue. The keyboard works (the arrow keys, Enter, Escape, the first letters), and Escape or a
// click outside closes the list. The Component knows nothing about hotels: the caller gives the choices and
// handles the change (for example, a new address).
//
// With `search`, a long list gets a search field at its top, with the look of the RateShopper property search. The
// field gets the focus when the list opens. The search ignores upper and lower case and accents, and finds a choice
// by its `searchText` (default: its title). A `pinned` choice stays on top in each search ("All properties").
//
// A choice can name its `section`: a gray heading shows over the first choice of each section ("Property groups",
// "Properties"). The list never ends with a heading: when the bottom edge would show a heading with no full choice under
// it, the list gets shorter, and the heading shows with its first choice when the user scrolls. With `action`, one blue
// line under the list runs an action ("Manage property groups"); it closes the list first.

export type TitleSelectOption = {
	id: string,
	label: string,
	// The second line of the choice, gray ("Piraeus · 5stelle reservations").
	detail?: string,
	// A 24 px outline icon before the texts: blue when the choice is selected, gray when not.
	icon?: React.ComponentType<React.SVGProps<SVGSVGElement>>,
	// The text that the search finds this choice by ("The Alex Monte Kastella Piraeus"). Default: the title.
	searchText?: string,
	// true: the search never hides this choice ("All properties").
	pinned?: boolean,
	// The heading of the part of the list that starts with this choice ("Property groups"). Give the choices of one
	// section one after the other.
	section?: string,
};

// The action line under the list.
export type TitleSelectAction = {
	label: string,
	// A 20 px solid icon before the label.
	icon?: React.ComponentType<React.SVGProps<SVGSVGElement>>,
	onClick: () => void,
};

export type TitleSelectSearch = {
	// The placeholder of the field ("Search your properties").
	placeholder: string,
	// The text when no choice matches ("No property matches").
	empty: string,
	// The field shows only when the list has more choices than this number. Default: 0 (always).
	minOptions?: number,
};

type Props = {
	options: TitleSelectOption[],
	// The id of the selected choice.
	value: string,
	onChange: (id: string) => void,
	// The name of the list for a screen reader ("Properties").
	listLabel: string,
	// The title. Default: the title of the selected choice.
	label?: string,
	// The second line under the title, gray ("Santikos Collection · 7 properties").
	sublabel?: string,
	// true: a plain title with no chevron and no list (a page with one choice only).
	disabled?: boolean,
	// A search field at the top of the list.
	search?: TitleSelectSearch,
	// One action line under the list ("Manage property groups").
	action?: TitleSelectAction,
};

const BUTTON = 'group flex min-w-0 max-w-full flex-col items-start rounded text-left focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 disabled:cursor-default';
const PANEL = 'z-50 w-80 max-w-full rounded-lg bg-white shadow-lg ring-1 ring-gray-900/5 focus:outline-none sm:w-96';

// Lower case, with no accents: "Città" and "citta" are equal.
export const foldText = (text: string): string => text.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

// The choices that a search shows: the pinned choices and the choices whose search text holds each word of the query.
export const filterOptions = (options: TitleSelectOption[], query: string): TitleSelectOption[] => {
	const words = foldText(query).split(/\s+/).filter(word => word !== '');
	if (words.length === 0) return options;
	return options.filter(option => {
		if (option.pinned === true) return true;
		const text = foldText(option.searchText ?? option.label);
		return words.every(word => text.includes(word));
	});
};

const TitleBlock = ({ title, sublabel, disabled, open }: { title: string, sublabel?: string, disabled: boolean, open: boolean }) => <>
	<span className="flex max-w-full items-center gap-x-1.5">
		<span className="truncate text-xl font-semibold leading-7 text-gray-900">{title}</span>
		{!disabled && <ChevronDownIcon aria-hidden="true"
			className={classNames('h-5 w-5 shrink-0 text-gray-500 transition-transform group-hover:text-gray-700', open ? 'rotate-180' : '')} />}
	</span>
	{sublabel !== undefined && <span className="block max-w-full truncate text-sm text-gray-500">{sublabel}</span>}
</>;

const OptionContent = ({ option, selected }: { option: TitleSelectOption, selected: boolean }) => {
	const Icon = option.icon;
	return <>
		{Icon !== undefined && <Icon aria-hidden="true" className={classNames('mt-0.5 h-5 w-5 shrink-0', selected ? 'text-blue-600' : 'text-gray-400')} />}
		<span className="min-w-0">
			<span className={classNames('block truncate text-sm font-semibold', selected ? 'text-blue-700' : 'text-gray-900')}>{option.label}</span>
			{option.detail !== undefined && <span className="block truncate text-xs text-gray-500">{option.detail}</span>}
		</span>
	</>;
};

// The heading over the first choice of a section. data-heading marks it for endOnRow.
const SECTION_HEADING = 'px-3 pb-1 text-xs font-semibold uppercase tracking-wide text-gray-500';
const headingBefore = (options: TitleSelectOption[], index: number): string | undefined => {
	const section = options[index].section;
	return section !== undefined && section !== options[index - 1]?.section ? section : undefined;
};

// A list never ends with a heading: when the last line that shows is a heading, or a heading over a cut choice, the list
// ends at the top edge of that heading. A heading in the top half of the list stays (the list would be too short).
export const endOnRow = (box: HTMLElement) => {
	const bottom = box.scrollTop + box.clientHeight;
	const lines = Array.from(box.children as HTMLCollectionOf<HTMLElement>);
	const last = [...lines].reverse().find(line => line.offsetTop < bottom);
	if (last === undefined) return;
	const cut = last.offsetTop + last.offsetHeight > bottom;
	const above = last.previousElementSibling as HTMLElement | null;
	const heading = last.dataset.heading !== undefined ? last : (cut && above?.dataset.heading !== undefined ? above : null);
	if (heading === null || heading.offsetTop - box.scrollTop < box.clientHeight / 2) return;
	box.style.maxHeight = `${heading.offsetTop - box.scrollTop}px`;
};

const ACTION_CLASS = 'flex w-full items-center gap-x-3 rounded-md px-3 py-2 text-left text-sm font-semibold text-blue-600';
const ActionContent = ({ action }: { action: TitleSelectAction }) => {
	const Icon = action.icon;
	return <>
		{Icon !== undefined && <Icon aria-hidden="true" className="h-5 w-5 shrink-0" />}
		{action.label}
	</>;
};

const optionClass = ({ focus, selected }: { focus: boolean, selected: boolean }) => classNames('flex cursor-pointer items-start gap-x-3 rounded-md px-3 py-1.5',
	selected ? 'bg-blue-50' : (focus ? 'bg-gray-50' : ''));

// The first active choice of a search list: the selected choice when the query is empty, else the first choice that
// matches (a pinned choice does not count), else the first choice.
export const firstActive = (shown: TitleSelectOption[], value: string, query: string): number => {
	if (foldText(query).trim() === '') return Math.max(0, shown.findIndex(option => option.id === value));
	return Math.max(0, shown.findIndex(option => option.pinned !== true));
};

// The list with the search field, in a panel under the title. The field keeps the focus: the arrow keys move the
// active choice, Enter picks it, Escape closes the panel (Popover). After each change of the query, the first match
// is active, so Enter picks the hotel that the user looks for, not the pinned choice on top.
const SearchList = ({ options, value, onChange, listLabel, search, close, action }: {
	options: TitleSelectOption[], value: string, onChange: (id: string) => void, listLabel: string, search: TitleSelectSearch, close: () => void,
	action?: TitleSelectAction,
}) => {
	const id = useId();
	const [query, setQuery] = useState('');
	const shown = useMemo(() => filterOptions(options, query), [options, query]);
	const [active, setActive] = useState(() => firstActive(shown, value, ''));
	const list = useRef<HTMLUListElement>(null);
	const matches = shown.some(option => option.pinned !== true);

	// At the open, the list starts at its top. When the selected choice is not in view there, the row above it goes to the
	// top edge of the list, so the list shows one full row above the selected choice and no row is cut under the field.
	// Near the end of the list, that position is past the end of the scroll, so the list goes to its end. There the top
	// edge can cut a row: the list then gets shorter by the part of that row that shows (less than one row).
	useLayoutEffect(() => {
		const box = list.current;
		if (box === null) return;
		scrollToSelected(box);
		endOnRow(box);
		// The open only (no dependencies), not each move of the active choice.
	}, []);

	// After a search the list starts at its top, so the pinned choice and the first heading show.
	const searched = useRef(false);
	useLayoutEffect(() => {
		const box = list.current;
		if (!searched.current || box === null) {
			searched.current = true;
			return;
		}
		box.style.removeProperty('max-height');
		box.scrollTop = 0;
		endOnRow(box);
	}, [query]);

	function scrollToSelected(box: HTMLUListElement) {
		const row = box.querySelector<HTMLElement>(`[data-index="${active}"]`);
		if (row === null) return;
		if (row.offsetTop + row.offsetHeight <= box.clientHeight) return;
		const top = (row.previousElementSibling as HTMLElement | null)?.offsetTop ?? row.offsetTop;
		const end = box.scrollHeight - box.clientHeight;
		if (top <= end) {
			box.scrollTop = top;
			return;
		}
		const cut = Array.from(box.children as HTMLCollectionOf<HTMLElement>)
			.find(child => child.offsetTop < end && child.offsetTop + child.offsetHeight > end);
		if (cut !== undefined) box.style.maxHeight = `${box.scrollHeight - cut.offsetTop - cut.offsetHeight}px`;
		box.scrollTop = box.scrollHeight;
	}

	useEffect(() => {
		list.current?.querySelector(`[data-index="${active}"]`)?.scrollIntoView({ block: 'nearest' });
	}, [active]);

	const pick = (option: TitleSelectOption | undefined) => {
		if (option === undefined) return;
		onChange(option.id);
		close();
	};
	const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
		if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
			event.preventDefault();
			const step = event.key === 'ArrowDown' ? 1 : -1;
			setActive(current => Math.min(shown.length - 1, Math.max(0, current + step)));
		} else if (event.key === 'Enter') {
			event.preventDefault();
			pick(shown[active]);
		}
	};

	return <>
		<div className="border-b border-gray-100 p-2">
			<div className="grid w-full grid-cols-1">
				{/* autoFocus: the list opens on a click of the title, and the search is the next step. */}
				<input type="search" autoFocus autoComplete="off" role="combobox" aria-expanded="true" aria-controls={`${id}-list`}
					aria-activedescendant={shown[active] === undefined ? undefined : `${id}-${active}`} aria-label={search.placeholder}
					placeholder={search.placeholder} value={query} onKeyDown={onKeyDown}
					onChange={event => {
						setQuery(event.target.value);
						setActive(firstActive(filterOptions(options, event.target.value), value, event.target.value));
					}}
					className="input col-start-1 row-start-1 block w-full rounded-md bg-white py-1.5 pl-10 pr-3 sm:text-sm/6" />
				<MagnifyingGlassIcon aria-hidden="true" className="pointer-events-none col-start-1 row-start-1 ml-3 h-5 w-5 self-center text-gray-400" />
			</div>
		</div>
		<ul ref={list} id={`${id}-list`} role="listbox" aria-label={listLabel} className="relative max-h-80 overflow-y-auto p-2">
			{shown.map((option, index) => {
				const selected = option.id === value;
				const heading = headingBefore(shown, index);
				return <React.Fragment key={option.id}>
					{heading !== undefined && <li role="presentation" data-heading=""
						className={classNames(SECTION_HEADING, index === 0 ? 'pt-1' : 'pt-3')}>{heading}</li>}
					<li id={`${id}-${index}`} data-index={index} role="option" aria-selected={selected}
						onMouseMove={() => setActive(index)} onMouseDown={event => event.preventDefault()} onClick={() => pick(option)}
						className={optionClass({ focus: index === active, selected })}>
						<OptionContent option={option} selected={selected} />
					</li>
				</React.Fragment>;
			})}
			{!matches && <li role="presentation" className="px-3 py-1.5 text-sm text-gray-500">{search.empty}</li>}
		</ul>
		{action !== undefined && <div className="border-t border-gray-100 p-2">
			<button type="button" onClick={() => { close(); action.onClick(); }}
				className={classNames(ACTION_CLASS, 'hover:bg-gray-50 focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-600')}>
				<ActionContent action={action} />
			</button>
		</div>}
	</>;
};

// The value of the action line in the plain list (a Listbox option that runs the action, not a choice).
const ACTION_VALUE = '\u0000action';

const TitleSelect: React.FunctionComponent<Props> = ({ options, value, onChange, listLabel, label, sublabel, disabled = false, search, action }) => {
	const title = label ?? options.find(o => o.id === value)?.label ?? '';

	if (search !== undefined && options.length > (search.minOptions ?? 0)) {
		// min-w-0: in a flex row the Popover box may shrink, so a long title is cut with an ellipsis (a phone).
		return <Popover className="min-w-0 max-w-full">
			{({ open }) => <>
				{/* The button holds both lines, so the list opens under the whole title block. */}
				<PopoverButton disabled={disabled} className={BUTTON}>
					<TitleBlock title={title} sublabel={sublabel} disabled={disabled} open={open} />
				</PopoverButton>
				<PopoverPanel anchor={{ to: 'bottom start', gap: 8 }} className={PANEL}>
					{({ close }) => <SearchList options={options} value={value} onChange={onChange} listLabel={listLabel} search={search}
						close={close} action={action} />}
				</PopoverPanel>
			</>}
		</Popover>;
	}

	const choose = (id: string) => id === ACTION_VALUE ? action?.onClick() : onChange(id);
	return <Listbox value={value} onChange={choose} disabled={disabled}>
		{/* The button holds both lines, so the list opens under the whole title block. */}
		<ListboxButton className={BUTTON}>
			{({ open }) => <TitleBlock title={title} sublabel={sublabel} disabled={disabled} open={open} />}
		</ListboxButton>
		<ListboxOptions anchor={{ to: 'bottom start', gap: 8 }} aria-label={listLabel} className={classNames(PANEL, 'max-h-96 overflow-y-auto p-2')}
			ref={(box: HTMLElement | null) => { if (box !== null) endOnRow(box); }}>
			{options.map((option, index) => {
				const heading = headingBefore(options, index);
				return <React.Fragment key={option.id}>
					{heading !== undefined && <div role="presentation" data-heading=""
						className={classNames(SECTION_HEADING, index === 0 ? 'pt-1' : 'pt-3')}>{heading}</div>}
					<ListboxOption value={option.id} className={optionClass}>
						{({ selected }) => <OptionContent option={option} selected={selected} />}
					</ListboxOption>
				</React.Fragment>;
			})}
			{/* In the plain list the action is the last option, under a line: the keyboard reaches it as a choice. */}
			{/* The line goes from edge to edge of the panel, as under the search list. */}
			{action !== undefined && <ListboxOption value={ACTION_VALUE} className="-mx-2 -mb-2 mt-2 cursor-pointer border-t border-gray-100 p-2">
				{({ focus }) => <span className={classNames(ACTION_CLASS, focus ? 'bg-gray-50' : '')}><ActionContent action={action} /></span>}
			</ListboxOption>}
		</ListboxOptions>
	</Listbox>;
};

export default TitleSelect;
